// src/mobile_pages/docs/Data.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Folder,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  ChevronRight,
  ChevronLeft,
  Search,
  Plus,
  MoreVertical,
  Download,
  Trash2,
  FolderPlus,
  UploadCloud,
  X,
  Home
} from "lucide-react";

import {
  getPapersTree,
  createPapersFolder,
  uploadPaperFile,
  getPaperFile,
  getPaperDownloadUrl,
  deletePapersFolder,
  deletePaperFile,
} from "../../api/papers";

// --- Helpers ---
const isImgExt = (name = "") => /\.(png|jpe?g|gif|bmp|webp|avif)$/i.test(name);
const isVidExt = (name = "") => /\.(mp4|webm|ogg|mov|m4v)$/i.test(name);
const isAudExt = (name = "") => /\.(mp3|wav|ogg|m4a|flac)$/i.test(name);
const isPdfExt = (name = "") => /\.pdf$/i.test(name);
const isTxtExt = (name = "") => /\.(txt|md|csv|json|log)$/i.test(name);

function bytes(maybe) {
  const n = Number(maybe || 0);
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${u[i]}`;
}

const SYSTEM_ROOT_NAMES = ["기본"];

export default function Data() {
  const { id: pid1, projectId: pid2 } = useParams();
  const projectId = pid1 || pid2;

  // Data State
  const [roots, setRoots] = useState([]); // Tree structure
  const [loading, setLoading] = useState(true);

  // Navigation State
  // history stack: array of { id: number | null, name: string }
  // null id represents "Root" (project level)
  const [history, setHistory] = useState([{ id: null, name: "자료실" }]);
  const currentFolder = history[history.length - 1]; // current view context

  // Selection / Modal State
  const [activeFile, setActiveFile] = useState(null); // File object for detail modal
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Floating action menu
  const [modalMode, setModalMode] = useState(null); // 'create-folder', 'upload'

  // Form State
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");

  // Search
  const [query, setQuery] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);

  // --- Data Loading ---
  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getPapersTree(projectId);
      setRoots(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTree();
    // projectId interceptor
    if (projectId) localStorage.setItem("projectId", projectId);
  }, [loadTree, projectId]);


  // --- Helper: Find Folder Node by ID in Tree ---
  const findFolderNode = useCallback((nodes, targetId) => {
    for (const node of nodes) {
      if (node.id === targetId) return node;
      if (node.children) {
        const found = findFolderNode(node.children, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // --- Derived State: Current Folder Content ---
  const currentFolderContent = useMemo(() => {
    if (currentFolder.id === null) {
      // Root view: show project roots
      return {
        folders: roots,
        files: []
      };
    }
    const node = findFolderNode(roots, currentFolder.id);
    return node ? { folders: node.children || [], files: node.files || [] } : { folders: [], files: [] };
  }, [roots, currentFolder, findFolderNode]);

  // --- Search Logic ---
  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const term = query.toLowerCase();
    const results = { folders: [], files: [] };

    const traverse = (nodes, pathName) => {
      for (const node of nodes) {
        const currentPath = pathName ? `${pathName} > ${node.name}` : node.name;

        // Check folder match
        if (node.name.toLowerCase().includes(term)) {
          results.folders.push({ ...node, path: pathName || "최상위" });
        }

        // Check files
        if (node.files) {
          for (const f of node.files) {
            if (f.name.toLowerCase().includes(term)) {
              results.files.push({ ...f, path: currentPath });
            }
          }
        }

        if (node.children) traverse(node.children, currentPath);
      }
    };

    traverse(roots, "");
    return results;
  }, [query, roots]);


  // --- Handlers ---
  const handleNavigate = (folder) => {
    setHistory(prev => [...prev, { id: folder.id, name: folder.name }]);
    setQuery(""); // Clear search on nav
    setIsSearchMode(false);
  };

  const handleBack = () => {
    if (history.length > 1) {
      setHistory(prev => prev.slice(0, prev.length - 1));
    }
  };

  const handleFileClick = async (file) => {
    setActiveFile(file);
    // Fetch full details if needed (often raw file obj is enough, but detailed info might need API)
    // If the file object from tree lacks 'file' url, we might need fetch.
    if (!file.file) {
      try {
        const res = await getPaperFile(file.id);
        setActiveFile(prev => ({ ...prev, ...res.data }));
      } catch (e) { console.error(e); }
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return alert("폴더명을 입력하세요.");
    try {
      await createPapersFolder({
        project: projectId,
        name: newFolderName,
        parent: currentFolder.id
      });
      await loadTree();
      setNewFolderName("");
      setModalMode(null);
      // alert("생성되었습니다.");
    } catch (e) {
      alert("폴더 생성 실패");
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return alert("파일을 선택하세요.");
    try {
      await uploadPaperFile({
        project: projectId,
        folder: currentFolder.id,
        file: uploadFile,
        name: uploadName || uploadFile.name
      });
      await loadTree();
      setUploadFile(null);
      setUploadName("");
      setModalMode(null);
      alert("업로드 완료");
    } catch (e) {
      alert("업로드 실패");
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (SYSTEM_ROOT_NAMES.includes(folder.name) && !folder.parent) {
      return alert("기본 폴더는 삭제할 수 없습니다.");
    }
    if (!window.confirm(`'${folder.name}' 폴더와 내부 파일이 모두 삭제됩니다. 계속하시겠습니까?`)) return;
    try {
      await deletePapersFolder(folder.id);
      await loadTree();
    } catch (e) {
      alert("삭제 실패");
    }
  };

  const handleDeleteFile = async () => {
    if (!activeFile) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deletePaperFile(activeFile.id);
      setActiveFile(null);
      await loadTree();
    } catch (e) {
      if (e?.response?.status === 400) {
        alert("문서에 연결된 파일은 삭제할 수 없습니다.");
      } else {
        alert("삭제 실패");
      }
    }
  };

  // --- Render ---
  const isRoot = history.length === 1;
  const displayContent = isSearchMode ? searchResults : currentFolderContent;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full flex flex-col">
      {/* Header */}
      <header className="bg-white px-5 py-4 border-b border-gray-200 sticky top-0 z-10 safe-area-top shadow-sm transition-all duration-200 ease-in-out">
        <div className="flex items-center gap-3 mb-1">
          {isSearchMode ? (
            <button onClick={() => { setIsSearchMode(false); setQuery(""); }} className="p-1 -ml-1 text-slate-500 active:text-slate-800">
              <ChevronLeft size={28} />
            </button>
          ) : (
            !isRoot && (
              <button onClick={handleBack} className="p-1 -ml-1 text-slate-500 active:text-slate-800">
                <ChevronLeft size={28} />
              </button>
            )
          )}
          <h1 className="text-2xl font-bold text-slate-900 truncate flex-1 leading-none pt-1">
            {isSearchMode ? "검색 결과" : currentFolder.name}
          </h1>
          {!isSearchMode && (
            <button onClick={() => setIsSearchMode(true)} className="p-2.5 bg-gray-50 rounded-full text-slate-500 hover:bg-gray-100 active:scale-95 transition-all">
              <Search size={22} />
            </button>
          )}
        </div>

        {/* Search Input */}
        {isSearchMode && (
          <div className="mt-3 animate-in fade-in slide-in-from-top-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="폴더 또는 파일명 검색"
              className="w-full bg-slate-100 rounded-2xl px-4 py-3 text-base outline-none text-slate-800 placeholder:text-slate-400"
            />
          </div>
        )}

        {/* Breadcrumb-ish info - Always render container to maintain height, text-transparent if root/search */}
        {!isSearchMode && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-400 mt-2 h-5 overflow-hidden">
            <Home size={12} className={isRoot ? "opacity-0" : ""} />
            <span className={isRoot ? "opacity-0" : ""}>
              {history.length > 1 ? history.map(h => h.name).join(" > ") : "Placeholder"}
            </span>
          </div>
        )}
      </header>

      {/* Content List */}
      <main className="flex-1 p-4 pb-24">
        {loading ? (
          <div className="py-12 text-center text-slate-400 text-sm">로딩 중...</div>
        ) : (
          <div className="space-y-3">
            {/* Folders */}
            {displayContent?.folders.map(folder => (
              <div key={`folder-${folder.id}`}
                onClick={() => handleNavigate(folder)}
                className="bg-white p-3 rounded-2xl border border-blue-50 shadow-sm flex items-center justify-between active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0">
                    <Folder size={20} fill="currentColor" className="opacity-20 text-blue-500" />
                    <Folder size={20} className="absolute" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate text-[15px]">{folder.name}</p>
                    <p className="text-[11px] text-slate-400">폴더</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Only allow deleting folders in normal view, not search, for safety */}
                  {!isSearchMode && !SYSTEM_ROOT_NAMES.includes(folder.name) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                      className="p-2 text-slate-300 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  <ChevronRight size={18} className="text-slate-300" />
                </div>
              </div>
            ))}

            {/* Files */}
            {displayContent?.files.map(file => {
              // Determine icon based on type
              let IconComp = FileText;
              let colorClass = "text-slate-500 bg-slate-50";
              if (isImgExt(file.name)) { IconComp = ImageIcon; colorClass = "text-purple-500 bg-purple-50"; }
              else if (isPdfExt(file.name)) { IconComp = FileText; colorClass = "text-red-500 bg-red-50"; }
              else if (isVidExt(file.name)) { IconComp = Video; colorClass = "text-rose-500 bg-rose-50"; }

              const pathInfo = file.path ? <p className="text-[10px] text-blue-400 mt-0.5">{file.path}</p> : null;

              return (
                <div key={`file-${file.id}`}
                  onClick={() => handleFileClick(file)}
                  className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-3 active:scale-[0.98] transition-transform"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                    <IconComp size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 truncate text-sm">{file.name}</p>
                    <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                      <span>{bytes(file.size)}</span>
                      <span>•</span>
                      <span>{new Date(file.created_at).toLocaleDateString()}</span>
                    </div>
                    {pathInfo}
                  </div>
                </div>
              );
            })}

            {/* Empty State */}
            {(!displayContent?.folders.length && !displayContent?.files.length) && (
              <div className="py-20 text-center">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FolderPlus size={32} className="text-slate-300" />
                </div>
                <p className="text-slate-400 text-sm">폴더가 비어있습니다.</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Floating Action Menu (Only in normal view and if folder selected) */}
      {!isSearchMode && currentFolder.id !== null && (
        <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end gap-3 safe-area-bottom">
          {isMenuOpen && (
            <>
              <button
                onClick={() => setModalMode("create-folder")}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border border-slate-100 text-sm font-semibold text-slate-700 animate-in slide-in-from-bottom-2 fade-in"
              >
                <FolderPlus size={18} className="text-blue-500" />
                새 폴더
              </button>
              <button
                onClick={() => setModalMode("upload")}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border border-slate-100 text-sm font-semibold text-slate-700 animate-in slide-in-from-bottom-2 fade-in"
              >
                <UploadCloud size={18} className="text-purple-500" />
                파일 업로드
              </button>
            </>
          )}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${isMenuOpen ? 'bg-slate-900 rotate-45' : 'bg-blue-600'}`}
          >
            <Plus size={28} className="text-white" />
          </button>
        </div>
      )}

      {/* Modal: File Details */}
      {activeFile && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white w-full max-w-sm rounded-[2rem] p-6 shadow-2xl animate-in slide-in-from-bottom-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900 truncate pr-4">{activeFile.name}</h3>
              <button onClick={() => setActiveFile(null)} className="p-2 bg-slate-100 rounded-full text-slate-500">
                <X size={20} />
              </button>
            </div>

            {/* Preview Area */}
            <div className="mb-6 rounded-2xl bg-slate-50 border border-slate-100 min-h-[150px] flex items-center justify-center overflow-hidden relative">
              {isImgExt(activeFile.name) ? (
                <img src={activeFile.file} alt="preview" className="max-h-[200px] w-auto object-contain" />
              ) : (
                <div className="text-center">
                  <FileText size={48} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">미리보기를 지원하지 않는 형식입니다</p>
                </div>
              )}
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-sm py-2 border-b border-slate-50">
                <span className="text-slate-500">크기</span>
                <span className="font-semibold">{bytes(activeFile.size)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-slate-50">
                <span className="text-slate-500">등록일</span>
                <span className="font-semibold">{new Date(activeFile.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between text-sm py-2 border-b border-slate-50">
                <span className="text-slate-500">출처</span>
                <span className="font-semibold">{activeFile.origin === 'doc' ? '문서 첨부' : '직접 업로드'}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <a
                href={getPaperDownloadUrl(activeFile.id)}
                className="flex-1 h-12 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <Download size={18} />
                다운로드
              </a>
              <button
                onClick={handleDeleteFile}
                className="w-12 h-12 rounded-xl bg-red-50 text-red-500 flex items-center justify-center active:scale-95 transition-transform"
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create Folder / Upload */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-6 animate-in fade-in">
          <div className="bg-white w-full max-w-xs rounded-3xl p-6 shadow-2xl">
            <h3 className="text-lg font-bold mb-4">
              {modalMode === 'create-folder' ? '새 폴더 만들기' : '파일 업로드'}
            </h3>

            {modalMode === 'create-folder' ? (
              <input
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="폴더 이름"
                className="w-full h-12 bg-slate-50 rounded-xl px-4 text-sm outline-none border border-slate-200 focus:border-blue-500 mb-4"
                autoFocus
              />
            ) : (
              <div className="space-y-3 mb-4">
                <div className="p-4 border-2 border-dashed border-slate-200 rounded-xl text-center bg-slate-50 relative">
                  <input
                    type="file"
                    onChange={e => setUploadFile(e.target.files[0])}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  {uploadFile ? (
                    <p className="text-sm font-semibold text-blue-600 truncate px-2">{uploadFile.name}</p>
                  ) : (
                    <div className="text-slate-400">
                      <UploadCloud size={24} className="mx-auto mb-1" />
                      <span className="text-xs">터치하여 파일 선택</span>
                    </div>
                  )}
                </div>
                {uploadFile && (
                  <input
                    value={uploadName}
                    onChange={e => setUploadName(e.target.value)}
                    placeholder="파일 이름 (선택사항)"
                    className="w-full h-10 bg-white rounded-lg px-3 text-xs outline-none border border-slate-200"
                  />
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setModalMode(null); setNewFolderName(""); setUploadFile(null); }}
                className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-600 font-semibold text-sm"
              >
                취소
              </button>
              <button
                onClick={modalMode === 'create-folder' ? handleCreateFolder : handleUpload}
                className="flex-1 h-11 rounded-xl bg-slate-900 text-white font-semibold text-sm"
              >
                {modalMode === 'create-folder' ? '생성' : '업로드'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
